import React, { useCallback, useEffect, useState } from 'react';
import { Fingerprint, ShieldCheck, Loader2, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

// WebAuthn helpers — the backend returns options in JSON with base64url fields.
// The browser expects raw ArrayBuffers for challenge/id, so we decode client-side.
const b64urlToBuffer = (str) => {
  const padded = str + '==='.slice((str.length + 3) % 4);
  const bin = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
};

const bufferToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const isSupported = typeof window !== 'undefined' && !!(window.PublicKeyCredential && navigator.credentials);

export default function BiometricEnrollCard() {
  const { toast } = useToast();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/webauthn/passkeys');
      setPasskeys(data.items || []);
    } catch (e) {
      // Silent — user might just not be authenticated.
      if (process.env.NODE_ENV === 'development') console.warn('[BiometricEnrollCard] load passkeys', e?.response?.status);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    if (!isSupported) {
      toast({ title: 'Non pris en charge', description: 'Votre navigateur ne supporte pas WebAuthn.' });
      return;
    }
    setEnrolling(true);
    try {
      const { data: opts } = await api.post('/webauthn/register/options');
      const publicKey = {
        ...opts,
        challenge: b64urlToBuffer(opts.challenge),
        user: { ...opts.user, id: b64urlToBuffer(opts.user.id) },
        excludeCredentials: (opts.excludeCredentials || []).map((c) => ({ ...c, id: b64urlToBuffer(c.id) })),
      };
      const credential = await navigator.credentials.create({ publicKey });
      if (!credential) throw new Error('Enrôlement annulé');

      const attestation = {
        id: credential.id,
        rawId: bufferToB64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToB64url(credential.response.clientDataJSON),
          attestationObject: bufferToB64url(credential.response.attestationObject),
        },
        clientExtensionResults: credential.getClientExtensionResults?.() || {},
      };

      const deviceName = navigator.userAgentData?.platform || navigator.platform || 'Cet appareil';
      await api.post('/webauthn/register/verify', { credential: attestation, deviceName });
      toast({ title: 'Passkey enregistrée 🎉', description: 'Cet appareil pourra désormais valider vos actions sensibles.' });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Enrôlement impossible';
      toast({ title: 'Échec de l\'enrôlement', description: String(detail) });
    } finally { setEnrolling(false); }
  };

  const removePasskey = async (id) => {
    if (!window.confirm('Supprimer cette passkey ? Vous devrez en enregistrer une nouvelle pour cet appareil.')) return;
    try {
      await api.delete(`/webauthn/passkeys/${id}`);
      toast({ title: 'Passkey supprimée' });
      await load();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de supprimer.' });
    }
  };

  return (
    <div className="rounded-2xl bg-[#1a1d1a] text-white p-6 sm:p-7 border border-white/10 shadow-xl" data-testid="biometric-enroll-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-medium text-[#5a8268]">
            <ShieldCheck className="w-3.5 h-3.5" /> Sécurité biométrique
          </div>
          <h3 className="mt-2 text-[20px] font-semibold leading-tight">Vos <span className="serif-italic text-[#d97757]">passkeys</span></h3>
          <p className="mt-1.5 text-[13px] text-white/60 max-w-md leading-relaxed">
            Ajoutez Touch ID, Windows Hello ou Android biometric pour sécuriser vos actions futures (retraits, changements sensibles).
          </p>
        </div>
        <div className="w-11 h-11 rounded-xl bg-[#d97757]/20 text-[#d97757] flex items-center justify-center shrink-0">
          <Fingerprint className="w-5 h-5" />
        </div>
      </div>

      {!isSupported && (
        <div className="mt-4 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-[12.5px] text-amber-200 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          Votre navigateur ne prend pas en charge WebAuthn. Utilisez Chrome, Safari, Firefox ou Edge récent.
        </div>
      )}

      <div className="mt-5">
        {loading ? (
          <div className="text-[13px] text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : passkeys.length === 0 ? (
          <div className="text-[13px] text-white/60">Aucune passkey enregistrée pour l'instant.</div>
        ) : (
          <ul className="space-y-2" data-testid="passkey-list">
            {passkeys.map((pk) => (
              <li key={pk.id} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3.5 py-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#5a8268]/20 text-[#5a8268] flex items-center justify-center">
                  <Fingerprint className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-medium truncate">{pk.deviceName}</div>
                  <div className="text-[11px] text-white/50">Enregistré le {new Date(pk.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removePasskey(pk.id)}
                  aria-label="Supprimer cette passkey"
                  data-testid={`passkey-delete-${pk.id}`}
                  className="w-8 h-8 rounded-lg hover:bg-rose-500/20 text-white/40 hover:text-rose-300 transition-colors flex items-center justify-center"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Button
        onClick={enroll}
        disabled={enrolling || !isSupported}
        data-testid="passkey-enroll-btn"
        className="btn-anim mt-5 w-full h-11 rounded-full bg-[#d97757] hover:bg-[#c66745] text-white text-[13.5px] font-medium"
      >
        {enrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
        Enrôler cet appareil
      </Button>
    </div>
  );
}
